import { classToPlain, Exclude, Expose, plainToClass, Transform } from "class-transformer";
import * as t from "io-ts";
import { validateJSON } from "../../web/validation";
import { TimeGroup, toTimeGroup } from "../time-group";
import { getDatabaseCodeTransformer } from "../transfomers/transformers";
import { Exportable } from "../exportable";
import { adjustFromTimeZone, format } from "../../date/date";

export const PipelineFlowTransportReportValidator = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    docType: t.string,
    url: t.string,
    filePath: t.string,
    regionId: t.string,
    fromDttm: t.number,
    toDttm: t.number,
    timeGroup: t.string,
    operatorId: t.string,
    vendorId: t.string,
    rateModelId: t.string,
  }),
  t.partial({
    operatorName: t.string,
    vendorName: t.string,
    rateModelName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const PipelineFlowTransportReportArrayValidator = t.array(
  PipelineFlowTransportReportValidator
);

export type PipelineFlowTransportReport = t.TypeOf<
  typeof PipelineFlowTransportReportValidator
>;

export type PipelineFlowTransportReportArray = t.TypeOf<
  typeof PipelineFlowTransportReportArrayValidator
>;

@Exclude()
export class PipelineFlowTransportReportEntity implements Exportable {
  @Expose()
  id!: string;
  @Expose()
  name!: string;
  @Expose()
  docType!: string;
  @Expose()
  url!: string;
  @Expose()
  filePath!: string;
  @Expose()
  regionId!: string;
  @Expose()
  fromDttm!: number;
  @Expose()
  toDttm!: number;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toTimeGroup))
  timeGroup!: TimeGroup;
  @Expose()
  operatorId!: string;
  @Expose()
  operatorName?: string;
  @Expose()
  vendorId!: string;
  @Expose()
  vendorName?: string;
  @Expose()
  rateModelId!: string;
  @Expose()
  rateModelName?: string;
  @Expose()
  createDttm?: number;
  @Expose()
  lastModifiedDttm?: number;

  /**
   * Constructor for PipelineFlowTransportReportEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(
    pipelineFlowTransportReport: PipelineFlowTransportReport
  ): PipelineFlowTransportReportEntity {
    return plainToClass(
      PipelineFlowTransportReportEntity,
      pipelineFlowTransportReport,
      {
        excludeExtraneousValues: true,
      }
    );
  }

  /**
   * Deserializes the PipelineFlowTransportReportEntity to a PipelineFlowTransportReport type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<PipelineFlowTransportReport> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(PipelineFlowTransportReportValidator, object);
  }

  readonly CSV_HEADERS: string[] = [
    'Name',
    'From Date',
    'To Date',
    'Time Group',
    'Operator',
    'Vendor',
    'Rate Model',
  ];

  asCsv(): string[] {
    return [
      this.name ?? '',
      format(adjustFromTimeZone(this.fromDttm), 'yyyy-MM-dd HH:mm:ss'),
      format(adjustFromTimeZone(this.toDttm), 'yyyy-MM-dd HH:mm:ss'),
      this.timeGroup.code,
      this.operatorName ?? '',
      this.vendorName ?? '',
      this.rateModelName ?? '',
    ];
  }
}

export interface PipelineFlowTransportReportSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  name?: string;
  regionIds?: string[];
  operatorIds?: string[];
  vendorIds?: string[];
  rateModelIds?: string[];
  timeGroups?: string[];
}
